import React, { useState, useEffect } from 'react';

const isValidUrl = (str) => {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
};

const getSize = (data) => (new Blob([JSON.stringify(data)]).size / 1024).toFixed(2);

const TreeNode = ({ nodeKey, value, level = 0, searchTerm }) => {
  const [open, setOpen] = useState(false);
  const isObject = typeof value === 'object' && value !== null;

  const lowerSearch = searchTerm.toLowerCase();
  const match = nodeKey.toLowerCase().includes(lowerSearch) || 
                (typeof value === 'string' && value.toLowerCase().includes(lowerSearch));

  if (searchTerm && !match && !isObject) return null;

  // Filter children recursively if object
  let children = [];
  if (isObject) {
    children = Object.entries(value)
      .map(([k, v]) => (
        <TreeNode key={k} nodeKey={k} value={v} level={level + 1} searchTerm={searchTerm} />
      ))
      .filter(Boolean);

    if (searchTerm && children.length === 0 && !match) return null;
  }

  return (
    <div className="ml-2" style={{ marginLeft: level * 16 }}>
      <div
        className="d-flex align-items-center"
        onClick={() => isObject && setOpen(!open)}
        style={{ cursor: isObject ? 'pointer' : 'default' }}
      >
        {isObject && (open ? <i className="bi bi-caret-down-square-fill me-2"></i>: <i className="bi bi-caret-right-square-fill me-2"></i>)}
        {!searchTerm ? (
  <strong className="ml-1">{nodeKey}:</strong>
) : (
  match && <strong className="ml-1" style={{ backgroundColor: 'black', color: 'white' }}>{nodeKey}:</strong>
)}&nbsp;
        {!isObject && (
  !searchTerm ? (
    <span>{String(value)}</span>
  ) : (
    match && <span style={{ backgroundColor: 'yellow', color: 'black' }}>{String(value)}</span>
  )
)}
      </div>
      {open && isObject && children}
    </div>
  );
};

const ApiResponce = ({ initialData = '' }) => {
  const [theme, setTheme] = useState('dark');
  const [rawInput, setRawInput] = useState(initialData);
  const [json, setJson] = useState(null);
  const [view, setView] = useState('auto');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    try {
      const parsed = typeof rawInput === 'string' ? JSON.parse(rawInput) : rawInput;
      setJson(parsed);
      setError('');
    } catch (e) {
      setJson(null);
      setError('Invalid JSON');
    }
  }, [rawInput]);

  const handleCopy = () => {
    const copyText = typeof rawInput === 'string' ? rawInput : JSON.stringify(rawInput, null, 2);
    navigator.clipboard.writeText(copyText).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1500);
    });
  };

  const handleUpload = (e) => {
    const reader = new FileReader();
    reader.onload = () => setRawInput(reader.result);
    if (e.target.files[0]) reader.readAsText(e.target.files[0]);
  };

  const detectView = () => {
    if (isValidUrl(rawInput)) return 'url';
    if (Array.isArray(json)) return 'table';
    if (typeof json === 'object') return 'tree';
    return 'pretty';
  };

  const renderTable = () => {
    if (!Array.isArray(json)) return <div>Not an array</div>;
    const headers = Object.keys(json[0] || {});
    return (
      <table className="table table-bordered table-sm mt-3">
        <thead className="thead-light">
          <tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {json.map((row, i) => (
            <tr key={i}>{headers.map((h) => <td key={h}>{JSON.stringify(row[h])}</td>)}</tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderContent = () => {
    if (error) return <div className="alert alert-danger mt-3">{error}</div>;
    if (!json) return null;

    const mode = view === 'auto' ? detectView() : view;

    switch (mode) {
      case 'pretty':
        return <pre className={`p-3 rounded ${theme === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}>{JSON.stringify(json, null, 2)}</pre>;
      case 'raw':
        return <pre className={`p-3 rounded ${theme === 'dark' ? 'bg-dark text-light' : ''}`}>{rawInput}</pre>;
      case 'tree':
        return (
          <div className="tree-view mt-2">
            {Object.entries(json).map(([k, v]) => (
              <TreeNode key={k} nodeKey={k} value={v} searchTerm={searchTerm} />
            ))}
          </div>
        );
          
      case 'table':
        return renderTable();
      case 'url':
        return (
          <div className="mt-2">
            <a href={rawInput} target="_blank" rel="noreferrer" className="btn btn-outline-primary">Open API URL</a>
          </div>
        );
      default:
        return <div>Unsupported view</div>;
    }
  };

  return (
    <div className={`container-fluid rounded pt-4 pb-4 ${theme === 'dark' ? '' : 'bg-light text-dark'}`}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={handleCopy}>
            {copySuccess ? <i className="bi bi-check-lg"></i> : <i className="bi bi-clipboard"></i>} Copy
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}>
            {theme === 'dark' ? <i className="bi bi-brightness-high-fill"></i> : <i className="bi bi-cloud-moon-fill"></i>}
          </button>
        </div>
      </div>

      {/* <textarea
        className={`form-control ${theme === 'dark' ? 'bg-dark text-light' : ''}`}
        rows={5}
        value={rawInput}
        onChange={(e) => setRawInput(e.target.value)}
        placeholder="Paste API response, JSON, or a URL..."
      /> */}

      <div className="row mt-3">
        <div className="col-md-6">
          <label>View Mode</label>
          <select className="form-control form-control-sm" value={view} onChange={(e) => setView(e.target.value)}>
            <option value="auto">Auto</option>
            <option value="raw">Raw</option>
            <option value="pretty">Pretty</option>
            <option value="tree">Tree</option>
            <option value="table">Table</option>
            <option value="url">URL</option>
          </select>
        </div>
        {view === 'tree' && (
          <div className="col-md-6">
            <label>Search (Tree)</label>
            <input
              className="form-control form-control-sm"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search keys..."
            />
          </div>
        )}
      </div>

      <div className="mt-3">
        {renderContent()}
      </div>

      {json && !error && (
        <div className="text-muted small mt-2">
          ✅ Valid JSON &nbsp;|&nbsp; Size: {getSize(json)} KB &nbsp;|&nbsp; Keys: {Object.keys(json).length}
        </div>
      )}
    </div>
  );
};

export default ApiResponce;